/**
 * Shared category visual mapping — the single source of truth for turning a
 * category label into an icon + tone, used by BOTH the dashboard transaction
 * feed ({@link AccountsView}) and the Spend Insights view ({@link InsightsView})
 * so the two screens stay visually cohesive.
 *
 * `categoryVisual(label)` maps a (case-insensitive) category label to
 * `{ Icon, tone }` where `tone` is one of `g0..g6`. The tone drives the
 * `.esb-cat--gN` CSS classes for the feed's icon circles.
 *
 * {@link TONE_HEX} mirrors the exact palette hexes defined in `styles.css`
 * (`.esb-cat--g0 .. --g6`). SVG `fill`/`stroke` need the literal colour value
 * (they cannot read a CSS class), so the donut chart and the Insights legend
 * reference these hexes directly. THESE MUST STAY IN SYNC WITH styles.css — if
 * a `.esb-cat--gN` colour changes there, change it here too.
 */

import {
  IconBills,
  IconDining,
  IconEntertainment,
  IconGeneric,
  IconGroceries,
  IconIncome,
  IconTransport,
} from './BrandLogo.js';

/** A category tone key. Matches the `.esb-cat--gN` classes in styles.css. */
export type CategoryTone = 'g0' | 'g1' | 'g2' | 'g3' | 'g4' | 'g5' | 'g6';

/**
 * Tone → hex map. MUST mirror the `.esb-cat--gN` background colours in
 * `packages/web/src/ui/styles.css` exactly (SVG fills need the literal value).
 * Keep these in sync with the CSS palette.
 */
export const TONE_HEX: Readonly<Record<CategoryTone, string>> = {
  g0: '#6b7d84', // generic / uncategorised — slate
  g1: '#06d6a0', // groceries — bright teal
  g2: '#2f6f9f', // transport — blue
  g3: '#b06a1f', // dining — amber-brown
  g4: '#7a4fb0', // bills — purple
  g5: '#1f8a5b', // income — green
  g6: '#b34774', // entertainment — magenta
};

/** Pick a category icon + accent tone from the (case-insensitive) label. */
export function categoryVisual(label: string): {
  Icon: (props: { readonly className?: string }) => JSX.Element;
  tone: CategoryTone;
} {
  const key = label.toLowerCase();
  if (/(grocer|food|supermarket|shop)/.test(key)) return { Icon: IconGroceries, tone: 'g1' };
  if (/(transport|fuel|petrol|uber|taxi|travel)/.test(key)) return { Icon: IconTransport, tone: 'g2' };
  if (/(dining|restaurant|cafe|coffee|eat)/.test(key)) return { Icon: IconDining, tone: 'g3' };
  if (/(bill|utilit|electric|water|rent|insur)/.test(key)) return { Icon: IconBills, tone: 'g4' };
  if (/(salary|income|deposit|refund|credit)/.test(key)) return { Icon: IconIncome, tone: 'g5' };
  if (/(entertain|movie|music|game|leisure)/.test(key)) return { Icon: IconEntertainment, tone: 'g6' };
  return { Icon: IconGeneric, tone: 'g0' };
}
